import {
  DrawerDescription,
  AlertDescription,
  DrawerHeader,
  FormCombobox,
  DrawerTitle,
  DrawerClose,
  Typography,
  Loading,
  Button,
  Alert,
  Form,
} from '@ui/components';
import { ArrowLeft, Cancel, Info } from '@ui/components/icons';
import { FC } from 'react';

import { useCurrentUserPermissionTransfer } from './use-current-user-permission-transfer';
import { CurrentUserTransferSubmitDialog } from './current-user-transfer-submit-dialog';

interface CurrentUserPermissionTransferProps {
  onClose: () => void;
}

export const CurrentUserPermissionTransfer: FC<CurrentUserPermissionTransferProps> = ({
  onClose,
}) => {
  const {
    isTransferRolePending,
    submitDialogOpen,
    selectedUser,
    userOptions,
    isLoading,
    form,
    t,
    setSubmitDialogOpen,
    onSubmit,
  } = useCurrentUserPermissionTransfer(onClose);

  const filter = (value: string, search: string, keywords?: string[]) => {
    if (!keywords?.length) {
      return value.toLowerCase().startsWith(search?.toLocaleLowerCase()) ? 1 : 0;
    }
    return keywords?.some((keyword) => keyword.toLowerCase().startsWith(search.toLowerCase()))
      ? 1
      : 0;
  };

  return (
    <>
      <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
        <Button onClick={onClose} variant="ghost" size="small" icon>
          <ArrowLeft />
        </Button>
        <DrawerTitle className="flex items-center">
          {t('CURRENT_USER_EDIT.ACCOUNT_OWNER')}
        </DrawerTitle>
        <DrawerClose asChild>
          <Button variant="ghost" size="small" icon>
            <Cancel />
          </Button>
        </DrawerClose>
      </DrawerHeader>
      <DrawerDescription />

      <form className="flex-1 overflow-auto flex flex-col p-4 pt-5 gap-2">
        <div className="space-y-5 flex-1">
          <Loading isLoading={isLoading} size="medium">
            <div className="space-y-4 flex-1 border-b border-border-weak pb-4">
              <Typography variant="labelMediumBold">
                {t('CURRENT_USER_EDIT.ACCOUNT_OWNER_NEW_ADMIN')}
              </Typography>
              <Form {...form}>
                <FormCombobox
                  placeholder={t('CURRENT_USER_EDIT.ACCOUNT_OWNER_CONTROL_PLACEHOLDER')}
                  emptyContent={t('CURRENT_USER_EDIT.ACCOUNT_OWNER_CONTROL_EMPTY')}
                  componentProps={{ filter }}
                  formControl={form.control}
                  options={userOptions}
                  name="id"
                />
              </Form>
              <Alert className="px-4 py-3" variant="default">
                <AlertDescription className="flex gap-3 items-start">
                  <div>
                    <Info className="text-surface" />
                  </div>
                  <div>
                    <Typography variant="labelSmallBook">
                      {t('CURRENT_USER_EDIT.ACCOUNT_OWNER_INFO')}
                    </Typography>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          </Loading>
        </div>
        <Button
          onClick={form.handleSubmit(() => setSubmitDialogOpen(true))}
          disabled={isLoading || isTransferRolePending}
          isLoading={isTransferRolePending}
          className="shrink-0"
          type="button"
          size="large"
          wide
        >
          {t('CURRENT_USER_EDIT.ACCOUNT_OWNER_TRANSFER_ROLE')}
        </Button>

        <CurrentUserTransferSubmitDialog
          name={selectedUser ? `${selectedUser?.firstName} ${selectedUser?.lastName}` : ''}
          onSubmit={form.handleSubmit(onSubmit)}
          onOpenChange={setSubmitDialogOpen}
          isLoading={isTransferRolePending}
          email={selectedUser?.email || ''}
          open={submitDialogOpen}
        />
      </form>
    </>
  );
};
